import type { Asset } from "schemas/generated/asset";
import type { ModifiableElementType } from "schemas/generated/fileModifiers";

import React, { useState } from "react";

import AssetsLibrary from "@/features/assets/components/AssetsLibrary";
import Popover from "@replo/design-system/components/popover/Popover";
import twMerge from "@replo/design-system/utils/twMerge";
import { ChevronUp, Plus } from "lucide-react";
import useMeasure from "react-use-measure";
import { useOverridableState } from "replo-runtime/shared/hooks/useOverridableState";

import { useDefaultSelectedElementDesignation } from "../store/ModifiersStoreContext";

interface AssetModifierProps {
  onAssetChange: (assetSource: string | null) => void;
}

export const AssetModifier: React.FC<AssetModifierProps> = ({
  onAssetChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [triggerRef] = useMeasure();

  const defaultDesignation = useDefaultSelectedElementDesignation();

  const [selectedAssetUrl, setSelectedAssetUrl] = useOverridableState<
    string | null
  >(defaultDesignation?.element.props?.src ?? null, onAssetChange);

  const assetUrl = selectedAssetUrl;

  const allowedAssetTypes: ("image" | "video")[] = (() => {
    if (defaultDesignation?.element.type === "image") {
      return ["image"];
    }
    if (defaultDesignation?.element.type === "video") {
      return ["video"];
    }
    return ["image", "video"];
  })();

  const handleAssetSelect = (asset: Asset) => {
    setSelectedAssetUrl(asset.url);
    setIsOpen(false);
  };

  const popoverContent = (
    <div
      className="flex flex-col overflow-hidden bg-white rounded-lg shadow-lg"
      style={{
        width: "calc(var(--radix-popover-trigger-width, 100%))",
        height: "400px",
      }}
    >
      <AssetsLibrary
        assetTypes={allowedAssetTypes}
        onAssetSelected={handleAssetSelect}
        shouldAllowActions={false}
      />
    </div>
  );

  return (
    <Popover
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      content={popoverContent}
      shouldPreventDefaultOnInteractOutside={false}
      side="bottom"
      align="start"
      variant="unstyled"
    >
      <div ref={triggerRef} className="w-full max-w-[388px]">
        <div
          className="flex flex-row items-center justify-between p-2 gap-2 w-full h-12 bg-surface border-[0.5px] border-border rounded-lg cursor-pointer hover:bg-background/50 transition-colors"
          onClick={() => setIsOpen(true)}
        >
          <div className="flex flex-row items-center gap-2">
            <div className="flex flex-row justify-center items-center w-8 h-8 bg-white border-[0.5px] border-dashed rounded-md overflow-hidden">
              {assetUrl ? (
                <AssetPreview
                  assetUrl={assetUrl}
                  elementType={defaultDesignation?.element.type}
                />
              ) : (
                <Plus className="w-4 h-4 text-muted" />
              )}
            </div>
            <span className="text-sm font-medium text-muted">
              {assetUrl ? "Change Media" : "Add Media"}
            </span>
          </div>
          <ChevronUp
            className={twMerge(
              "text-muted transition-transform",
              isOpen && "rotate-180",
            )}
            size={12}
          />
        </div>
      </div>
    </Popover>
  );
};

const AssetPreview: React.FC<{
  assetUrl: string;
  elementType?: ModifiableElementType;
}> = ({ assetUrl, elementType }) => {
  if (elementType === "video") {
    return (
      <video
        src={assetUrl}
        className="object-cover rounded-sm w-full h-full"
        preload="metadata"
        muted
      />
    );
  }
  return (
    <img
      src={assetUrl}
      alt="Selected media"
      className="object-cover rounded-sm w-full h-full"
    />
  );
};
